import { useContext, useMemo } from "react";
import { Spinner } from "@/components/ui/spinner";
import type { InputResponse } from "eve/client";
import { MessageCircleQuestionIcon } from "lucide-react";
import {
  Questionnaire,
  QuestionnaireActions,
  QuestionnaireChoice,
  QuestionnaireChoiceDescription,
  QuestionnaireChoices,
  QuestionnaireError,
  QuestionnaireInput,
  QuestionnaireItem,
  QuestionnaireNext,
  QuestionnairePrevious,
  QuestionnaireProgress,
  QuestionnaireSubmit,
  QuestionnaireTitle,
} from "@/components/ui/questionnaire";
import { Item, ItemHeader } from "@/components/ui/item";
import { AskContext } from "./asking";
import { MarkdownMessage } from "./MarkdownMessage";
import { isFreeform, type QuestionBlock } from "./projection";
import { subagentLabel } from "./toolLabels";

/** A submitted value: one of the question's option ids, or typed text. */
function toResponse(question: QuestionBlock, value: string): InputResponse {
  return question.request.options?.some((o) => o.id === value)
    ? { requestId: question.id, optionId: value }
    : { requestId: question.id, text: value.trim() };
}

/**
 * The questions Amy (or one of her subagents) is parked on, answered as ONE
 * questionnaire (shadcn Questionnaire): a step per question — its options,
 * plus a typed answer when the question allows one — and every answer goes
 * out in a single send, since eve resumes only once all of them are in.
 * A lone pick-one question answers on the click, like a button.
 */
export function AmyQuestionnaire({ questions }: { questions: QuestionBlock[] }) {
  const ask = useContext(AskContext);
  const single = questions.length === 1;
  const instant = single && !isFreeform(questions[0]!.request);
  const delivering = questions.some((q) => ask.answering[q.id]);
  const enabled =
    ask.canAnswer && !ask.readOnly && !delivering && questions.every((q) => q.status === "parked");
  const askedBy = questions[0]!.askedBy;

  // Keyed on the questions' content, not the array: parents rebuild the
  // array on every render, and a new `items` must never reset the step.
  const signature = questions
    .map((q) => `${q.id}:${(q.request.options ?? []).map((o) => o.id).join(",")}`)
    .join("|");
  const items = useMemo(
    () =>
      questions.map((q) => ({
        name: q.id,
        required: true,
        choices: q.request.options?.map((o) => ({ value: o.id })),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature]
  );

  const submit = (form: HTMLFormElement) => {
    if (!enabled) return;
    const data = new FormData(form);
    const responses = questions.flatMap((q) => {
      const value = data.get(q.id);
      return typeof value === "string" && value.trim() ? [toResponse(q, value)] : [];
    });
    if (responses.length === questions.length) ask.onAnswerMany(responses);
  };

  return (
    <Item variant="outline" size="sm" className="w-full flex-col flex-nowrap items-stretch gap-3 bg-background border-none">
      <Questionnaire
        items={items}
        shortcuts={ask.keyboardTarget && questions.some((q) => q.id === ask.keyboardTarget) ? "numbers" : undefined}
        onSubmit={(e) => {
          e.preventDefault();
          submit(e.currentTarget);
        }}
        className="gap-3"
      >
        <ItemHeader className="basis-auto justify-start gap-2 text-xs font-medium text-muted-foreground">
          <MessageCircleQuestionIcon className="size-3.5" />
          <span className="flex-1">
            {askedBy ? `${subagentLabel(askedBy)} asks` : "Amy needs your answer"}
          </span>
          {!single && <QuestionnaireProgress className="min-w-0 text-right" />}
        </ItemHeader>

        {questions.map((q) => {
          const options = q.request.options ?? [];
          const freeform = isFreeform(q.request);
          const input = freeform && (
            <QuestionnaireInput
              aria-label="Your answer"
              placeholder={options.length > 0 ? "Or type your own answer…" : "Type your answer…"}
            />
          );
          return (
            <QuestionnaireItem key={q.id} name={q.id} required disabled={!enabled} className="gap-3">
              <QuestionnaireTitle render={<div />} className="text-sm font-normal text-foreground">
                <MarkdownMessage content={q.request.prompt} />
              </QuestionnaireTitle>
              {options.length > 0 ? (
                <QuestionnaireChoices>
                  {options.map((o) => (
                    <QuestionnaireChoice
                      key={o.id}
                      value={o.id}
                      // A lone pick-one question answers on the pick. Straight
                      // to the answer, not via submit: inside this handler the
                      // questionnaire hasn't recorded the pick yet, so its
                      // required-check would still fail.
                      onChange={
                        instant
                          ? () => enabled && ask.onAnswerMany([{ requestId: q.id, optionId: o.id }])
                          : undefined
                      }
                    >
                      <span className={o.style === "danger" ? "block text-sm font-medium text-destructive" : "block text-sm font-medium"}>
                        {o.label}
                      </span>
                      {o.description && (
                        <QuestionnaireChoiceDescription>{o.description}</QuestionnaireChoiceDescription>
                      )}
                    </QuestionnaireChoice>
                  ))}
                  {input}
                </QuestionnaireChoices>
              ) : (
                input
              )}
              <QuestionnaireError />
            </QuestionnaireItem>
          );
        })}

        {!instant && (
          <QuestionnaireActions>
            <QuestionnairePrevious size="sm" disabled={!enabled}>
              Back
            </QuestionnairePrevious>
            <QuestionnaireNext size="sm" disabled={!enabled} />
            <QuestionnaireSubmit size="sm" disabled={!enabled}>
              {delivering && <Spinner />}
              {single ? "Send" : "Send answers"}
            </QuestionnaireSubmit>
          </QuestionnaireActions>
        )}
        {instant && delivering && (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Spinner className="size-3" />
            Sending…
          </p>
        )}
      </Questionnaire>
    </Item>
  );
}
